import { ReactNode } from 'react';
import { useLoadingStates } from '@/hooks/useLoadingStates';
import { ModalLoadingSkeleton } from '@/components/ui/modal-loading-skeleton';
import { IntelligentSkeleton } from '@/components/transitions/IntelligentSkeleton';
import { Button } from '@/components/ui/button';
import { ModalFooter } from '@/components/ui/modal-footer';
import { NoFamilyMessage } from '@/components/NoFamilyMessage';

interface ConditionalWrapperProps {
  children: ReactNode;
  type?: 'modal' | 'page';
  skeletonType?: 'form' | 'event' | 'person' | 'task' | 'contact' | 'dashboard' | 'list' | 'calendar' | 'profile';
  variant?: 'tasks' | 'calendar' | 'persons' | 'documents' | 'default';
  onClose?: () => void;
  requiresFamily?: boolean;
  customNoFamilyContent?: ReactNode;
}

/**
 * Intelligent wrapper that handles loading states and family requirements
 * Shows skeletons during loading and only shows "no family" when appropriate
 */
export const ConditionalWrapper = ({
  children,
  type = 'page',
  skeletonType = 'form',
  variant = 'default',
  onClose,
  requiresFamily = true,
  customNoFamilyContent,
}: ConditionalWrapperProps) => {
  const { shouldShowSkeleton, shouldShowNoFamily, hasUser } = useLoadingStates();

  // Show skeleton during loading
  if (shouldShowSkeleton) {
    if (type === 'modal') {
      return (
        <>
          <div className="flex-1 overflow-y-auto">
            <ModalLoadingSkeleton type={skeletonType as any} />
          </div>
        </>
      );
    } else {
      return (
        <div className="min-h-screen bg-page-gradient flex items-center justify-center">
          <IntelligentSkeleton type={skeletonType as any} />
        </div>
      );
    }
  }

  // If not authenticated, let the app handle auth redirect
  if (!hasUser) {
    return <>{children}</>;
  }

  // Show "no family" message only when required and truly no family exists
  if (requiresFamily && shouldShowNoFamily) {
    if (customNoFamilyContent) {
      return <>{customNoFamilyContent}</>;
    }

    if (type === 'modal') {
      return (
        <>
          <div className="flex-1 overflow-y-auto p-6 text-center">
            <h1 className="text-xl font-bold mb-2">Créer une famille</h1>
            <p className="text-muted-foreground mb-4">
              Vous devez d'abord créer une famille pour utiliser cette fonctionnalité
            </p>
          </div>
          
          <ModalFooter
            primaryLabel="Fermer"
            onPrimary={onClose}
            onCancel={onClose}
            variant={variant}
          />
        </>
      );
    } else {
      return (
        <NoFamilyMessage 
          description={variant === "persons" ? "Vous devez d'abord créer une famille pour ajouter les membres de votre famille" : "Vous devez d'abord créer une famille pour utiliser cette fonctionnalité"}
          buttonText="Créer ma famille"
          variant={variant === "persons" ? "persons" : variant as any}
        />
      );
    }
  }

  // All conditions met, show the actual content
  return <>{children}</>;
};